import { ReactNode } from 'react';

import { Box, BoxProps, Tooltip, useTheme } from '@sbv-clients/ui-shared';

const iconPaths = {
  pencil: (
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="m13.5 6.5 4 4" />
    </>
  ),
  dropdown: <path d="m6 9 6 6 6-6" />,
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
    </>
  ),
};

export const GridCellFill = ({
  color,
  children,
}: {
  color?: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: 'calc(100% + 20px)',
      height: '100%',
      mx: '-10px',
      px: '10px',
      boxSizing: 'border-box',
      backgroundColor: color,
    }}
  >
    {children}
  </Box>
);

export const HoverIconCell = ({
  value,
  icon,
  editable,
  onIconClick,
  showTooltip = false,
  sx,
}: {
  value: ReactNode;
  icon: 'pencil' | 'dropdown' | 'calendar';
  editable: boolean;
  onIconClick?: () => void;
  showTooltip?: boolean;
  sx?: BoxProps;
}) => {
  const theme = useTheme();

  const text = (
    <Box
      component="span"
      sx={{
        flex: '0 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        '& > svg, & > .MuiSvgIcon-root': { display: 'block' },
      }}
    >
      {value}
    </Box>
  );
  const valueBox = showTooltip ? (
    <Tooltip title={value} placement="bottom-start" arrow>
      {text}
    </Tooltip>
  ) : (
    text
  );

  if (!editable) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          ...sx,
        }}
      >
        {valueBox}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: 'calc(100% + 16px)',
          height: 30,
          mx: '-8px',
          px: '8px',
          boxSizing: 'border-box',
          border: '1px solid transparent',
          borderRadius: '7px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          transition: 'border-color 120ms, background-color 120ms',
          '.MuiDataGrid-cell:hover &, .MuiDataGrid-cell:focus-visible &': {
            // Transparent, so a selected row keeps its blue behind the box.
            borderColor: theme.custom?.controlBorder,
          },
          '.MuiDataGrid-row.Mui-selected .MuiDataGrid-cell:hover &, .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell:focus-visible &':
            { borderColor: theme.custom?.tableHeaderText },
          ...sx,
        }}
      >
        {valueBox}
        <Box
          component="span"
          aria-hidden
          onClick={(e) => {
            if (onIconClick) {
              e.stopPropagation();
              onIconClick();
            }
          }}
          sx={{
            width: 18,
            height: 18,
            ml: 'auto',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            color: theme.custom?.tableHeaderText,
            opacity: 0,
            transition: 'opacity 120ms',
            '.MuiDataGrid-cell:hover &, .MuiDataGrid-cell:focus-visible &': {
              opacity: 1,
            },
            '& svg': {
              width: 14,
              height: 14,
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 1.8,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            },
          }}
        >
          <svg viewBox="0 0 24 24">{iconPaths[icon]}</svg>
        </Box>
      </Box>
    </Box>
  );
};
